import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ApiEndpoint, CdnDashboardFilter, DashboardEndpoint, FormActionType, FormType } from '@enums';
import { RootState, useAppSelector } from '@hooks';

import type {
  FilterState,
  FormMetadata,
  FormPayload,
  FormSliceState,
  FormState,
  PaginationProps,
  SortableFormItem,
} from '@types';

const EMPTY_ARRAY: any[] = [];

const CDN_FORMS = {
  pagination: {
    [ApiEndpoint.DEFAULT]: {
      page_size: 5,
    },
  },
};

const CDN_DASHBOARD_FILTER: { filter: FilterState } = {
  filter: {
    searchTerm: '',
    filterCriteria: [
      {
        from: DashboardEndpoint.TOTAL_TRAFFIC,
        label: CdnDashboardFilter.Time,
        value: '15m',
      },
      {
        from: DashboardEndpoint.TOTAL_TRAFFIC,
        label: CdnDashboardFilter.Domain,
        value: 'all',
      },
    ],
  },
};

const formConfigs = new Map<FormType, Partial<FormState>>([
  [FormType.CdnOrigin, { ...CDN_FORMS }],
  [FormType.CdnRoute, { ...CDN_FORMS }],
  [FormType.CdnDashboard, { ...CDN_DASHBOARD_FILTER }],
]);

const defaultFormState: FormState = {
  init: true,
  loading: {},
  submitting: {},
  sortLoading: false,
  saveSuccess: false,
  deleteSuccess: false,
  formList: { [ApiEndpoint.DEFAULT]: [] },
  formContent: { [ApiEndpoint.DEFAULT]: {} },
  sortingList: [],
  filter: {
    searchTerm: '',
    filterCriteria: [{ label: 'status', value: 'all' }],
  },
  selectedIds: [],
  pagination: {
    [ApiEndpoint.DEFAULT]: {
      first_page: 1,
      last_page: 1,
      next_page: 1,
      page: 1,
      total: 0,
      total_pages: 0,
      page_size: 10,
    },
  },
  procedure: FormActionType.Idle,
  error: { [ApiEndpoint.DEFAULT]: null },
  lastUpdated: 0,
};

const initialState: FormSliceState = Object.values(FormType).reduce((acc, type) => {
  const customConfig = formConfigs.get(type as FormType);

  acc[type as FormType] = {
    ...defaultFormState,
    ...(customConfig && {
      filter: {
        ...defaultFormState.filter,
        ...customConfig.filter,
      },
      pagination: {
        ...defaultFormState.pagination,
        ...customConfig.pagination,
      },
    }),
  };

  return acc;
}, {} as FormSliceState);

const formSlice = createSlice({
  name: 'form',
  initialState,
  reducers: {
    fetchFormList: (state, { payload }: PayloadAction<FormPayload>) => {
      const { formType, globalOptions: { endpoint = ApiEndpoint.DEFAULT } = {} } = payload;
      state[formType].loading[endpoint] = true;
      state[formType].formList = {
        ...state[formType].formList,
        [endpoint]: state[formType].formList?.[endpoint] ?? [],
      };
    },
    fetchFormListSuccess: (
      state,
      {
        payload,
      }: PayloadAction<
        FormMetadata & {
          content?: boolean;
          lastUpdated: number;
          unique?: boolean;
          uniqueId?: string | number;
          list: any[];
          paginationInfo?: PaginationProps;
        }
      >,
    ) => {
      const { formType, lastUpdated, endpoint, unique, uniqueId, content = false, list, paginationInfo } = payload;
      state[formType].init = false;
      if (!endpoint) {
        return;
      }
      state[formType].loading[endpoint] = false;
      const targetStore = content ? 'formContent' : 'formList';
      state[formType][targetStore][endpoint] =
        unique && uniqueId
          ? {
              ...state[formType][targetStore][endpoint],
              [uniqueId]: list,
            }
          : list;
      state[formType].pagination[endpoint] = {
        ...state[formType].pagination[endpoint],
        ...paginationInfo,
      };
      state[formType].lastUpdated = lastUpdated;
    },
    fetchFormListError: (state, { payload }: PayloadAction<FormMetadata & { responseResult: any }>) => {
      const { formType, endpoint = ApiEndpoint.DEFAULT, responseResult } = payload;
      if (state[formType] && endpoint) {
        state[formType].init = false;
        state[formType].loading[endpoint] = false;
        state[formType].error[endpoint] = responseResult;
      }
    },
    fetchDashboardGraph: (state, { payload }: PayloadAction<any>) => {},
    updateSortingList(state, { payload }: PayloadAction<FormMetadata & { list: SortableFormItem[] }>) {
      const { formType, list } = payload;
      if (state[formType]) {
        state[formType].sortingList = list.map((item, index) => ({
          id: item.id,
          name: item.name,
          sort_order: index + 1,
          original_id: item.original_id,
        }));
      }
    },
    fetchSortingListSuccess(state, { payload }: PayloadAction<FormMetadata & { responseResult: any }>) {
      const { formType, endpoint = ApiEndpoint.DEFAULT, responseResult } = payload;
      if (state[formType] && endpoint) {
        state[formType].loading[endpoint] = false;
        state[formType].sortingList = responseResult;
      }
    },
    submitFormRequest: (state, { payload }: PayloadAction<FormPayload>) => {
      const { formType, globalOptions: { endpoint = ApiEndpoint.DEFAULT } = {} } = payload;
      state[formType].submitting[endpoint] = true;
    },
    submitFormSuccess: (state, { payload }: PayloadAction<FormMetadata & { content?: typeof defaultFormState }>) => {
      const { formType, endpoint = ApiEndpoint.DEFAULT, content = defaultFormState.formContent } = payload;
      if (state[formType] && endpoint) {
        state[formType].error[endpoint] = null;
        state[formType].submitting[endpoint] = false;
        state[formType].formContent[endpoint] = content;
        state[formType].saveSuccess = true;
      }
    },
    submitFormError: (state, { payload }: PayloadAction<FormMetadata & { responseResult: any }>) => {
      const { formType, endpoint, responseResult } = payload;
      if (state[formType] && endpoint) {
        state[formType].submitting[endpoint] = false;
        state[formType].error[endpoint] = responseResult;
      }
    },
    updateFormFilter: (state, { payload }: PayloadAction<FormMetadata & { value: any }>) => {
      const { formType, endpoint = ApiEndpoint.DEFAULT, value } = payload;
      const currentCriteria = state[formType].filter.filterCriteria;

      const newFilterCriteria = currentCriteria.map((criterion) => {
        if (criterion.from === endpoint) {
          const newValue = value.find((v) => v.label === criterion.label);
          if (newValue) {
            return {
              ...criterion,
              value: newValue.value,
            };
          }
        }
        return criterion;
      });
      state[formType].filter = {
        ...state[formType].filter,
        filterCriteria: newFilterCriteria,
      };
    },
    updateFormContent: (state, { payload }: PayloadAction<FormMetadata & { content: any }>) => {
      const { formType, endpoint = ApiEndpoint.DEFAULT, content } = payload;
      state[formType].formContent[endpoint] = content;
    },
    updateFormProcedure: (state, { payload }: PayloadAction<FormMetadata & { procedure: FormActionType }>) => {
      const { formType, procedure } = payload;
      state[formType].procedure = procedure;
    },
    updateFormPages: (state, { payload }: PayloadAction<FormPayload & { page: number }>) => {
      const { formType, globalOptions = {}, page } = payload;
      const endpoint = globalOptions.endpoint ?? ApiEndpoint.DEFAULT;
      state[formType].pagination[endpoint] = {
        ...state[formType].pagination[endpoint],
        page,
      };
    },
    updateFormPageSize: (state, { payload }: PayloadAction<FormPayload & { page_size: number }>) => {
      const { formType, globalOptions = {}, page_size } = payload;
      const endpoint = globalOptions.endpoint ?? ApiEndpoint.DEFAULT;
      state[formType].pagination[endpoint] = {
        ...state[formType].pagination[endpoint],
        page_size,
        page: 1,
      };
    },
    updateSelectedIds: (state, { payload }: PayloadAction<FormMetadata & { id: string | string[] }>) => {
      const { formType, id } = payload;
      const selected = state[formType].selectedIds;

      if (Array.isArray(id)) {
        state[formType].selectedIds = JSON.stringify(id) === JSON.stringify(selected) ? [] : id;
      } else {
        const index = selected.indexOf(id);
        if (index === -1) {
          selected.push(id);
        } else {
          selected.splice(index, 1);
        }
      }
    },
    deleteFormContent: (state, { payload }: PayloadAction<FormPayload>) => {
      const { formType, globalOptions: { endpoint = ApiEndpoint.DEFAULT } = {} } = payload;
      if (state[formType]) {
        state[formType].loading[endpoint] = true;
      }
    },
    deleteFormContentError: (state, { payload }: PayloadAction<FormMetadata & { error: any }>) => {
      const { formType, endpoint = ApiEndpoint.DEFAULT, error } = payload;
      if (state[formType] && endpoint) {
        state[formType].loading[endpoint] = false;
        state[formType].error[endpoint] = error;
      }
    },
    resetSubmitFormState: (state, { payload }: PayloadAction<FormMetadata>) => {
      const { formType, endpoint = ApiEndpoint.DEFAULT } = payload;

      if (state[formType]) {
        state[formType].submitting = defaultFormState.submitting;
        state[formType].saveSuccess = defaultFormState.saveSuccess;

        if (endpoint) {
          state[formType].error[endpoint] = defaultFormState.error[endpoint];
        }
      }
    },
    resetFormPagination: (state, { payload }: PayloadAction<FormType>) => {
      if (state[payload]) {
        state[payload].pagination = initialState[payload].pagination;
      }
    },
    resetFormData: (state, { payload }: PayloadAction<any>) => {
      const { uniqueId, formType, endpoint = ApiEndpoint.DEFAULT, resetType = 'list' } = payload;

      if (uniqueId) {
        if (resetType === 'content' || resetType === 'both') {
          if (!state[formType].formContent[endpoint]) {
            state[formType].formContent[endpoint] = {};
          }
          if (typeof state[formType].formContent[endpoint] === 'object') {
            state[formType].formContent[endpoint][uniqueId] = {};
          }
        }
        if (resetType === 'list' || resetType === 'both') {
          if (!state[formType].formList[endpoint]) {
            state[formType].formList[endpoint] = {};
          }
          if (typeof state[formType].formList[endpoint] === 'object') {
            state[formType].formList[endpoint][uniqueId] = [];
          }
        }
      } else {
        if (resetType === 'content' || resetType === 'both') {
          state[formType].formContent[endpoint] = {};
        }
        if (resetType === 'list' || resetType === 'both') {
          state[formType].formList[endpoint] = {};
        }
      }
    },
    resetDashboardFilter: (state, { payload }: PayloadAction<FormType>) => {
      if (state[payload]) {
        state[payload].filter = CDN_DASHBOARD_FILTER.filter;
      }
    },
    resetCurrentForm: (state, { payload }: PayloadAction<FormType>) => {
      state[payload] = { ...defaultFormState };
    },
    resetAllForms: () => initialState,
    cancelAutoReload: () => {},
  },
});

export const {
  fetchFormList,
  fetchFormListSuccess,
  fetchFormListError,
  fetchDashboardGraph,
  fetchSortingListSuccess,
  submitFormRequest,
  submitFormSuccess,
  submitFormError,
  updateSortingList,
  resetFormPagination,
  updateFormFilter,
  updateFormContent,
  updateFormProcedure,
  updateFormPages,
  updateFormPageSize,
  updateSelectedIds,
  deleteFormContent,
  deleteFormContentError,
  resetSubmitFormState,
  resetCurrentForm,
  resetDashboardFilter,
  resetFormData,
  resetAllForms,
  cancelAutoReload,
} = formSlice.actions;

export default formSlice.reducer;

export const useFormState = (formType: FormType) => useAppSelector((state: RootState) => state.form[formType]);
export const useFormInit = (formType: FormType) => useAppSelector((state: RootState) => state.form[formType].init);
export const useFormLoadingStatus = ({ formType, endpoint = ApiEndpoint.DEFAULT }: FormMetadata) =>
  useAppSelector((state: RootState) => state.form[formType]?.loading?.[endpoint] ?? false);
export const useFormLoading = ({ formType }: { formType: FormType }) =>
  useAppSelector((state: RootState) => state.form[formType]?.loading);
export const useFormSubmissionStatus = ({ formType, endpoint = ApiEndpoint.DEFAULT }: FormMetadata) =>
  useAppSelector((state: RootState) => state.form[formType]?.submitting?.[endpoint] ?? false);
export const useFormSaveStatus = (formType: FormType) =>
  useAppSelector((state: RootState) => state.form[formType].saveSuccess);
export const useFormSortStatus = (formType: FormType) =>
  useAppSelector((state: RootState) => state.form[formType].sortLoading);
export const useFormAllList = ({ formType }: { formType: FormType }) =>
  useAppSelector((state: RootState) => state.form[formType].formList ?? EMPTY_ARRAY);
export const useFormList = ({ formType, endpoint = ApiEndpoint.DEFAULT }: FormMetadata) =>
  useAppSelector((state: RootState) => state.form[formType].formList?.[endpoint] ?? EMPTY_ARRAY);
export const useFormContent = ({ formType, endpoint = ApiEndpoint.DEFAULT }: FormMetadata) =>
  useAppSelector((state: RootState) => state.form[formType].formContent[endpoint]);

export const useFormLastUpdated = (formType: FormType) =>
  useAppSelector((state: RootState) => state.form[formType].lastUpdated);
export const useFormSortedList = (formType: FormType) =>
  useAppSelector((state: RootState) => state.form[formType].sortingList);
export const useFormFilter = (formType: FormType) => useAppSelector((state: RootState) => state.form[formType].filter);
export const useFormFilterCriteria = ({ formType, endpoint }: FormMetadata) =>
  useAppSelector((state: RootState) => state.form[formType].filter.filterCriteria);
export const useFormSelectedList = (formType: FormType) =>
  useAppSelector((state: RootState) => state.form[formType].selectedIds);
export const useFormPagination = ({
  formType,
  endpoint = ApiEndpoint.DEFAULT,
}: {
  formType: FormType;
  endpoint?: ApiEndpoint | DashboardEndpoint;
}) => useAppSelector((state: RootState) => state.form[formType].pagination[endpoint]);
export const useFormProcedure = (formType: FormType) =>
  useAppSelector((state: RootState) => state.form[formType].procedure);
export const useFormError = ({ formType, endpoint = ApiEndpoint.DEFAULT }: FormMetadata) =>
  useAppSelector((state: RootState) => state.form[formType].error[endpoint] ?? null);
